import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Plus, MessageCircle, StickyNote, Pencil, GraduationCap, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { LEAD_STAGES, LEAD_STAGE, fmtDateTime, waLink, money, selectCls, btnBrand } from "@/lib/academyFormat";
import LeadModal from "@/components/academy/LeadModal";
import LeadNotesModal from "@/components/academy/LeadNotesModal";
import EnrollModal from "@/components/academy/EnrollModal";
import { ACADEMY } from "@/constants/testIds";

// Fix U (2026-10-13): interesados de la academia, por etapa, con notas,
// boton de WhatsApp y paso a inscripcion.
export default function AcademyLeadsPage() {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const isAdmin = user?.role === "admin";
  const stage = params.get("etapa") || "";
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [courses, setCourses] = useState([]);
  const [executives, setExecutives] = useState([]);
  const [sources, setSources] = useState([]);
  const [academyName, setAcademyName] = useState("AAME Academy");
  const [editing, setEditing] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [notesLead, setNotesLead] = useState(null);
  const [enrollLead, setEnrollLead] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/academy/leads");
      setLeads(data);
    } catch (err) {
      toast({ title: "Error al cargar interesados", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    api.get("/academy/courses").then(({ data }) => setCourses(data)).catch(() => {});
    api.get("/academy/settings").then(({ data }) => { setSources(data.lead_sources || []); setAcademyName(data.academy_name || "AAME Academy"); }).catch(() => {});
    if (isAdmin) api.get("/academy/staff").then(({ data }) => setExecutives(data.filter((s) => s.academy_roles.includes("executive")))).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const replace = (lead) => setLeads((ls) => {
    const exists = ls.some((l) => l.id === lead.id);
    return exists ? ls.map((l) => (l.id === lead.id ? lead : l)) : [lead, ...ls];
  });

  const changeStage = async (lead, value) => {
    try {
      const { data } = await api.put(`/academy/leads/${lead.id}`, { stage: value });
      replace(data);
    } catch (err) {
      toast({ title: "Error", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    }
  };

  const term = q.trim().toLowerCase();
  const visible = leads.filter((l) => (!stage || l.stage === stage)
    && (!term || [l.name, l.phone, l.email].some((v) => (v || "").toLowerCase().includes(term))));
  const count = (key) => leads.filter((l) => l.stage === key).length;
  const setStage = (key) => setParams(key ? { etapa: key } : {});

  return (
    <div className="space-y-6" data-testid={ACADEMY.leadsPage}>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-serif text-3xl sm:text-4xl tracking-tight text-foreground">Interesados</h1>
          <p className="text-base text-muted-foreground mt-1">{isAdmin ? "Todos los interesados de la academia." : "Tus interesados."}</p>
        </div>
        <Button className={btnBrand} onClick={() => { setEditing(null); setModalOpen(true); }} data-testid={ACADEMY.leadsAddButton}>
          <Plus className="w-4 h-4" /> Nuevo interesado
        </Button>
      </div>

      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={() => setStage("")} className={`px-3 py-1.5 rounded-full text-sm border ${!stage ? "border-brand text-brand bg-brand-light" : "border-border text-muted-foreground bg-white"}`} data-testid={`${ACADEMY.leadsStageFilterPrefix}all`}>
          Todos ({leads.length})
        </button>
        {LEAD_STAGES.map((s) => (
          <button key={s.key} type="button" onClick={() => setStage(s.key)} className={`px-3 py-1.5 rounded-full text-sm border ${stage === s.key ? "border-brand text-brand bg-brand-light" : "border-border text-muted-foreground bg-white"}`} data-testid={`${ACADEMY.leadsStageFilterPrefix}${s.key}`}>
            {s.label} ({count(s.key)})
          </button>
        ))}
      </div>
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre, teléfono o email" className="max-w-sm bg-white" data-testid={ACADEMY.leadsSearchInput} />

      {loading ? (
        <p className="text-center text-muted-foreground py-12">Cargando...</p>
      ) : visible.length === 0 ? (
        <div className="bg-white border border-border rounded-xl p-12 text-center" data-testid={ACADEMY.leadsEmpty}>
          <UserPlus className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground">No hay interesados {stage ? `en "${LEAD_STAGE[stage]?.label}"` : "cargados"}.</p>
        </div>
      ) : (
        <div className="bg-white border border-border rounded-xl overflow-x-auto">
          <table className="w-full text-sm" data-testid={ACADEMY.leadsTable}>
            <thead>
              <tr className="text-xs uppercase tracking-[0.1em] text-muted-foreground bg-[#E9F3F3]">
                <th className="text-left font-medium py-3 px-4">Interesado</th>
                <th className="text-left font-medium py-3 px-3 hidden md:table-cell">Curso</th>
                <th className="text-left font-medium py-3 px-3">Etapa</th>
                {isAdmin && <th className="text-left font-medium py-3 px-3 hidden lg:table-cell">Ejecutivo</th>}
                <th className="text-left font-medium py-3 px-3 hidden lg:table-cell">Último contacto</th>
                <th className="text-right font-medium py-3 px-4">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((l) => {
                const wa = waLink(l.phone, `Hola ${l.name.split(" ")[0]}, te escribo de ${academyName}.`);
                const lastNote = (l.notes || [])[l.notes.length - 1];
                return (
                  <tr key={l.id} className="border-t border-border align-top" data-testid={`${ACADEMY.leadRowPrefix}${l.id}`}>
                    <td className="py-3 px-4">
                      <p className="font-medium text-foreground">{l.name}</p>
                      <p className="text-xs text-muted-foreground">{l.phone}{l.email ? ` · ${l.email}` : ""}</p>
                      {l.source && <p className="text-xs text-muted-foreground">Llegó por {l.source}</p>}
                    </td>
                    <td className="py-3 px-3 hidden md:table-cell">{l.course_name || "—"}</td>
                    <td className="py-3 px-3">
                      <select className={`${selectCls} h-8 w-auto text-xs ${LEAD_STAGE[l.stage]?.cls || ""}`} value={l.stage} onChange={(e) => changeStage(l, e.target.value)} data-testid={`${ACADEMY.leadStageSelectPrefix}${l.id}`}>
                        {LEAD_STAGES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                      </select>
                    </td>
                    {isAdmin && <td className="py-3 px-3 hidden lg:table-cell">{l.executive_name || "Sin asignar"}</td>}
                    <td className="py-3 px-3 hidden lg:table-cell text-xs text-muted-foreground max-w-[220px]">
                      {lastNote ? <><span className="line-clamp-2 text-foreground">{lastNote.text}</span>{fmtDateTime(lastNote.at)}</> : "—"}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex justify-end gap-1 flex-wrap">
                        {wa && (
                          <a href={wa} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center h-9 w-9 rounded-md hover:bg-muted text-[#25A244]" title="Abrir WhatsApp" data-testid={`${ACADEMY.leadWhatsappPrefix}${l.id}`}>
                            <MessageCircle className="w-4 h-4" />
                          </a>
                        )}
                        <Button variant="ghost" size="icon" title="Notas" onClick={() => setNotesLead(l)} data-testid={`${ACADEMY.leadNotesButtonPrefix}${l.id}`}><StickyNote className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="icon" title="Editar" onClick={() => { setEditing(l); setModalOpen(true); }} data-testid={`${ACADEMY.leadEditButtonPrefix}${l.id}`}><Pencil className="w-4 h-4" /></Button>
                        {l.student_id ? (
                          <Button variant="outline" size="sm" onClick={() => navigate(`/academia/alumnos/${l.student_id}`)}>Ver alumno</Button>
                        ) : (
                          <Button size="sm" className={btnBrand} onClick={() => setEnrollLead(l)} data-testid={`${ACADEMY.leadEnrollButtonPrefix}${l.id}`}><GraduationCap className="w-4 h-4" /> Inscribir</Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <LeadModal
        open={modalOpen} onOpenChange={setModalOpen} lead={editing} courses={courses} executives={executives}
        sources={sources} canAssign={isAdmin} onSaved={(l) => { replace(l); toast({ title: editing ? "Interesado actualizado" : "Interesado cargado" }); }}
      />
      <LeadNotesModal open={Boolean(notesLead)} onOpenChange={(o) => !o && setNotesLead(null)} lead={notesLead} onSaved={(l) => { replace(l); setNotesLead(l); }} />
      <EnrollModal
        open={Boolean(enrollLead)} onOpenChange={(o) => !o && setEnrollLead(null)} lead={enrollLead}
        onDone={(enr) => { toast({ title: "Inscripción creada", description: `${enr.course_name}, total ${money(enr.total)}` }); navigate(`/academia/alumnos/${enr.student.id}`); }}
      />
    </div>
  );
}
